import { Check } from 'lucide-react';
import { cn } from './utils/cn';

export interface Step {
  key: string;
  label: string;
  description?: string;
}

export interface StepperProps {
  steps: Step[];
  /** Index of the currently active step (0-based). */
  current: number;
  /** Optional click handler for completed steps. */
  onStepClick?: (index: number) => void;
  className?: string;
}

export function Stepper({ steps, current, onStepClick, className }: StepperProps) {
  return (
    <ol className={cn('flex w-full items-stretch', className)}>
      {steps.map((step, idx) => {
        const isDone = idx < current;
        const isActive = idx === current;
        const isLast = idx === steps.length - 1;
        const clickable = isDone && onStepClick;

        return (
          <li key={step.key} className="flex-1 flex items-stretch">
            <div className="flex flex-col items-center text-center min-w-0">
              <button
                type="button"
                onClick={clickable ? () => onStepClick(idx) : undefined}
                disabled={!clickable}
                aria-current={isActive ? 'step' : undefined}
                className={cn(
                  'h-9 w-9 rounded-full border-2 grid place-items-center font-semibold text-sm',
                  'transition-all duration-200',
                  isDone && 'bg-brand-600 border-brand-600 text-white',
                  isActive && 'bg-surface border-brand-600 text-brand-700 ring-4 ring-brand-100',
                  !isDone && !isActive && 'bg-surface border-border text-fg-subtle',
                  clickable && 'hover:scale-105 cursor-pointer',
                )}
              >
                {isDone ? <Check className="h-4 w-4" /> : idx + 1}
              </button>
              <div className="mt-2 px-2 min-w-0">
                <div
                  className={cn(
                    'text-sm font-medium truncate',
                    isActive ? 'text-fg' : 'text-fg-muted',
                  )}
                >
                  {step.label}
                </div>
                {step.description ? (
                  <div className="text-xs text-fg-subtle mt-0.5 hidden sm:block">
                    {step.description}
                  </div>
                ) : null}
              </div>
            </div>
            {!isLast && (
              <div
                className={cn(
                  'flex-1 h-0.5 mt-[18px] mx-2 rounded-full',
                  idx < current ? 'bg-brand-600' : 'bg-border',
                )}
                aria-hidden
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
